import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import axios from 'axios';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api';

const API_BASE_URL = typeof apiConfig === 'string' ? apiConfig : NAMED_API_BASE_URL || 'https://pms.burrayhr.com';

const Shimmer = ({ width = '100%', height = '1rem', className = '', rounded = '4px' }) => (
    <span
        className={`placeholder placeholder-wave col-12 ${className}`}
        style={{ width, height, minHeight: height, borderRadius: rounded, display: 'block' }}
    />
);

const HODProfile = () => {
    const baseUrl = API_BASE_URL;
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(true);
    const [completedGoals, setCompletedGoals] = useState([]);

    useEffect(() => {
        const fetchProfile = async () => {
            setLoading(true);
            setError('');
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/users/me`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                setProfile(response.data);

                if (response.data?.employee?.id) {
                    const goalsResponse = await axios.get(`${baseUrl}/api/users/me/`, {
                        headers: { 'Authorization': `Bearer ${token}` },
                        params: {
                            employee: response.data.employee.id,
                            status: 'completed'
                        }
                    });

                    const empData = goalsResponse.data?.employee || goalsResponse.data || {};
                    const allGoals = Array.isArray(empData.goals) ? empData.goals : [];
                    const completed = allGoals.filter(goal => goal.status === 'completed' || goal.status === 'complete');
                    setCompletedGoals(completed);
                }
            } catch (err) {
                console.error('Error fetching profile:', err);
                setError('Failed to load profile.');
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [baseUrl]);

    // Fetch analytics for the employee once profile is loaded
    useEffect(() => {
        const fetchAnalytics = async () => {
            if (!profile?.employee?.id) return;
            setAnalyticsLoading(true);
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/performance-analytics`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    params: { employee: profile.employee.id }
                });
                setAnalytics(response.data);
            } catch (err) {
                console.error('Error fetching analytics:', err);
                setAnalytics(null);
            } finally {
                setAnalyticsLoading(false);
            }
        };
        if (profile) {
            fetchAnalytics();
        }
    }, [profile, baseUrl]);

    const stats = analytics?.overall_statistics || {};
    const timeliness = analytics?.overall_statistics || {};

    // Transform quarterly_trends object to array for recharts
    const quarterlyTrendsData = useMemo(() => {
        if (!analytics?.quarterly_trends) return [];
        return Object.entries(analytics.quarterly_trends).map(([quarter, dataStats]) => ({
            quarter,
            ...dataStats
        }));
    }, [analytics]);

    return (
        <div
            data-sidebar-skin="dark"
            data-header-skin="light"
            data-navbar-brand-skin="dark"
            data-sidebar-state="expand"
        >
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content pb-0">
                    {/* Header with Employee Profile Badge */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Performance Overview</h4>
                            <p className="text-muted mb-0 fs-13">Overview of your performance metrics and KPIs</p>
                        </div>

                        {loading ? (
                            <div className="placeholder-glow" style={{ width: '220px' }}>
                                <Shimmer width="100%" height="48px" rounded="8px" />
                            </div>
                        ) : profile ? (
                            <div className="bg-white border rounded p-3 shadow-sm d-flex align-items-center gap-3">
                                <div className="avatar avatar-md bg-primary-transparent text-primary rounded-circle d-flex align-items-center justify-content-center">
                                    <i className="ti ti-user fs-20"></i>
                                </div>
                                <div>
                                    <div className="fw-bold text-dark fs-15">
                                        {profile?.employee?.first_name} {profile?.employee?.last_name}
                                    </div>
                                    <div className="text-primary fs-12 fw-medium">
                                        {profile?.employee?.employee_id || profile?.employee?.code ? `Code: ${profile?.employee?.employee_id || profile?.employee?.code}` : ''}
                                    </div>
                                    <div className="text-muted fs-12">
                                        {profile?.employee?.position || 'HOD'}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Stats Grid */}
                    <div className="row g-3 mb-4">
                        {/* Completed KPIs */}
                        <div className="col-md-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    {analyticsLoading ? (
                                        <div className="placeholder-glow">
                                            <Shimmer width="40px" height="40px" rounded="8px" className="mb-2" />
                                            <Shimmer width="60%" height="14px" className="mb-1" />
                                            <Shimmer width="40%" height="24px" />
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <p className="text-muted fs-13 mb-1">Completed KPIs</p>
                                                <h3 className="fw-bold mb-0 text-dark">
                                                    {stats.completed_goals ?? 0} / {stats.total_goals ?? 0}
                                                </h3>
                                            </div>
                                            <div className="avatar avatar-lg bg-warning-transparent rounded-circle d-flex align-items-center justify-content-center text-warning">
                                                <i className="ti ti-target fs-24"></i>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Avg Performance */}
                        <div className="col-md-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    {analyticsLoading ? (
                                        <div className="placeholder-glow">
                                            <Shimmer width="40px" height="40px" rounded="8px" className="mb-2" />
                                            <Shimmer width="60%" height="14px" className="mb-1" />
                                            <Shimmer width="40%" height="24px" />
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <p className="text-muted fs-13 mb-1">Avg Performance</p>
                                                <h3 className="fw-bold mb-0 text-dark">
                                                    {stats.average_progress !== undefined ? `${stats.average_progress}%` : '0%'}
                                                </h3>
                                            </div>
                                            <div className="avatar avatar-lg bg-success-transparent rounded-circle d-flex align-items-center justify-content-center text-success">
                                                <i className="ti ti-trending-up fs-24"></i>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Timeliness */}
                        <div className="col-md-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    {analyticsLoading ? (
                                        <div className="placeholder-glow">
                                            <Shimmer width="40px" height="40px" rounded="8px" className="mb-2" />
                                            <Shimmer width="60%" height="14px" className="mb-1" />
                                            <Shimmer width="40%" height="24px" />
                                        </div>
                                    ) : (
                                        <div>
                                            <p className="text-muted fs-13 mb-2">KPIs Completion Timeliness</p>
                                            <div className="d-flex align-items-center gap-1 flex-wrap">
                                                <span className="badge bg-success text-white px-2 py-1 fs-11">
                                                    On Time: {timeliness.on_time_rate != null ? timeliness.on_time_rate : 0}%
                                                </span>
                                                <span className="badge bg-primary text-white px-2 py-1 fs-11">
                                                    Early: {timeliness.early_rate != null ? timeliness.early_rate : 0}%
                                                </span>
                                                <span className="badge bg-danger text-white px-2 py-1 fs-11">
                                                    Late: {timeliness.late_rate != null ? timeliness.late_rate : 0}%
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Chart Section */}
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white py-3 border-bottom">
                            <h6 className="card-title mb-0 fw-semibold text-dark">Quarterly Performance Trends</h6>
                        </div>
                        <div className="card-body">
                            {analyticsLoading ? (
                                <div className="placeholder-glow py-4">
                                    <Shimmer width="100%" height="280px" rounded="8px" />
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={quarterlyTrendsData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="colorQuarterly" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.8}/>
                                                <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="quarter" />
                                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                                        <Tooltip formatter={(value) => `${value}%`} />
                                        <Area
                                            type="monotone"
                                            dataKey="completion_rate"
                                            stroke="#4F46E5"
                                            fillOpacity={1}
                                            fill="url(#colorQuarterly)"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* Completed KPIs Table */}
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                            <h6 className="card-title mb-0 fw-semibold text-dark">Completed KPIs</h6>
                            <Link to="/hod/my-goals-reviews" className="btn btn-sm btn-primary">
                                View All
                            </Link>
                        </div>
                        <div className="card-body p-0">
                            {completedGoals.length === 0 ? (
                                <div className="text-center py-4 text-muted fs-14">
                                    No completed KPIs found.
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Activity / KPI</th>
                                                <th>Performance Standard / Target</th>
                                                <th>Weight / Metric</th>
                                                <th className="text-center">Self Score</th>
                                                <th className="text-center">HOD Score</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {completedGoals.slice(0, 5).map((goal, idx) => (
                                                <tr key={goal.id || idx}>
                                                    <td className="fw-medium">{goal.activity || goal.goal || '-'}</td>
                                                    <td className="text-primary fw-medium">{goal.performance_standard || goal.target || '-'}</td>
                                                    <td className="text-success fw-semibold">{goal.metric !== undefined ? `${goal.metric}%` : '-'}</td>
                                                    <td className="text-center fw-semibold">{goal.score !== undefined ? goal.score : '-'}</td>
                                                    <td className="text-center fw-semibold">{goal.hod_score !== undefined ? goal.hod_score : '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
                    </p>
                </div>
            </div>
        </div>
    );
};

export default HODProfile;